import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import ParticipantPopover from 'components/threepp/assessment/Question/ParticipantPopover/ParticipantPopover';
import { debounce } from 'lodash';
import { compose, mapProps } from 'recompose';
import useIsInViewport from 'utils/getIsInViewport/getIsInViewport';
import { showErrorMessage } from '../Alert/Alert';
import ActionsHOC from '../../threepp/assessment/Actions/ActionsHOC';
import './CategoryItem.scss';

export const CategoryItem = props => {
  const {
    assessment,
    category,
    updateAssigneesForCategory,
    onBulkAssignChange,
    setSelection,
    t,
    isAssessmentReadOnly,
    categoryAclAction,
    visibleCategory,
    categoryNameRef
  } = props;
  const { fullyAssignedUsers } = category.metadata;
  const { partiallyAssignedUsers } = category.metadata;
  const assessmentParticipants = assessment.participants;
  const categoryName = category.name;
  const categoryOrder = category.order;
  const [styleName, setStyleName] = useState('unselected');
  const { isInViewport } = useIsInViewport(categoryNameRef);

  const updateAssigneesForCategoryFn = (addedParticipants, removedParticipants) => {
    if (addedParticipants.length > 0 || removedParticipants.length > 0) {
      updateAssigneesForCategory({
        variables: {
          assessmentId: assessment.id,
          categoryId: category.id,
          emailsToAdd: addedParticipants,
          emailsToRemove: removedParticipants
        }
      })
        .then(() => {
          onBulkAssignChange(category.id, addedParticipants, removedParticipants);
        })
        .catch(error => {
          console.error(error);
          showErrorMessage(t('assessment.categories.assignmentErrorMsg'));
        });
    }
  };

  const debouncedUpdateAssignees = debounce((addedParticipants, removedParticipants) => {
    updateAssigneesForCategoryFn(addedParticipants, removedParticipants);
  }, 2500);

  const scrollIntoView = useCallback(() => {
    categoryNameRef && categoryNameRef.current.scrollIntoView({ block: 'end' });
  }, [categoryNameRef]);

  useEffect(() => {
    if (visibleCategory) {
      visibleCategory === category.id ? setStyleName('selected') : setStyleName('unselected');
      visibleCategory === category.id && !isInViewport && scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCategory, category.id]);

  return (
    <div id="individual-section" ref={categoryNameRef} className={`flexbox-container-${styleName}`}>
      {categoryAclAction !== 'Hide' && (
        <div className="section-item">
          <p className={`section-item-name-${styleName}`} onClick={() => setSelection(categoryName, categoryOrder)}>
            {categoryName}
          </p>
          <ParticipantPopover
            disabled={isAssessmentReadOnly}
            header="section assignment"
            initialAssignees={fullyAssignedUsers}
            initialPartialAssignees={partiallyAssignedUsers}
            assessmentParticipants={assessmentParticipants}
            onAssigneesChanged={debouncedUpdateAssignees}
            hideIfNoParticipants={false}
          />
        </div>
      )}
    </div>
  );
};

const CategoryItemHOC = compose(
  ActionsHOC,
  mapProps(props => ({
    categoryAclAction: props.aclAction,
    assessment: props.assessment,
    category: props.category,
    updateAssigneesForCategory: props.updateAssigneesForCategory,
    onBulkAssignChange: props.onBulkAssignChange,
    selection: props.selection,
    setSelection: props.setSelection,
    t: props.t,
    isAssessmentReadOnly: props.isAssessmentReadOnly,
    actions: props.actions,
    visibleCategory: props.visibleCategory,
    ref: props.categoryNameRef
  }))
)(forwardRef(({ ...props }, ref) => <CategoryItem {...props} categoryNameRef={ref} />));

export { CategoryItemHOC };
